import React, { Component } from 'react';
import { events } from '../../util/Events';
import { SearchResultsTrainingRow } from './SearchResultsTrainingRow';
import filter from 'lodash/filter';
import { TrainingSearchResult } from '../../models/Examentraining';

interface ComponentProps {
  block: any;
  type: 'distance' | 'datum_start';
  allResults: TrainingSearchResult[];
  isActive: boolean;
}

interface ComponentState {
  viewAll: boolean;
  limit: number;
  distance: string | null;
}

/**
 * This component is a group of trainings on a specific tutor location
 */
export class SearchResultsBlock extends Component<ComponentProps, ComponentState> {

  constructor(props: ComponentProps) {
    super(props);

    this.state = {
      viewAll: false,
      limit: (this.props.type === 'distance') ? 3 : 6,
      distance: null,
    };
  }

  /**
     * Toggle the hidden trainings
     */
  public toggleRows(): void {
    this.setState({ viewAll: !this.state.viewAll });
  }

  public componentDidMount(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const cityQuery = urlParams.get('city');

    if (cityQuery) {
      this.checkQueryStringForCity(cityQuery);
    } else {
      this.setState({ distance: null });
    }
  }


  private async checkQueryStringForCity(cityQuery: string): Promise<void> {
    const apiKey = 'AIzaSyAx37KXOgn9MRHQuHoVItl5SkMCTDXispA';
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(cityQuery)}&key=${apiKey}`
      );
      const data = await response.json();

      if (data.results.length > 0) {
        const userLat = data.results[0].geometry.location.lat;
        const userLong = data.results[0].geometry.location.lng;

        const locLat = parseFloat(this.props.block.loc_lat);
        const locLong = parseFloat(this.props.block.loc_long);
        const distance = this.calculateDistance(userLat, userLong, locLat, locLong);
        this.setState({ distance: distance });
      } else {
        console.error('Invalid or not found address.');
        this.setState({ distance: null });
      }
    } catch (error) {
      console.error('Error:', error);
      this.setState({ distance: null });
    }
  }

  private calculateDistance(userLat: number, userLong: number, locLat: number, locLong: number): string {
    const rad = (x: number): number => (x * Math.PI) / 180;
    const R = 6371;
    const dLat = rad(locLat - userLat);
    const dLon = rad(locLong - userLong);
    const a
            = Math.sin(dLat / 2) * Math.sin(dLat / 2)
            + Math.cos(rad(userLat)) * Math.cos(rad(locLat)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distanceKm = R * c;

    return `${distanceKm.toFixed(1)} km`;
  }


  public render(): JSX.Element {
    const { viewAll, limit } = this.state;
    const { block, allResults, type, isActive } = this.props;

    /** Get training dates for this location */
    let trainingDates = filter(allResults, thisResult => {
      if (type === 'distance') {
        return thisResult.loc_naam === block.loc_naam;
      }
      return thisResult.dateGroup === block.dateGroup;

    });

    /** Show all rows or show limited amount */
    const trainingRows = trainingDates.map((training, key) => {
      if (key < ((viewAll) ? 9999999 : limit)) {
        return (<SearchResultsTrainingRow training={training} key={key} type={type} />);
      }
    });

    let className = isActive ? 'search-results__item search-results__item--active' : 'search-results__item';
    const locAdress = block.loc_straat + ' ' + block.loc_nr;
    const locUrl = 'https://lyceo.nl/vestigingen/' + block.loc_stad + '/';
    const org_style = { display: 'none' };
    if (type === 'distance') {
      const query = encodeURIComponent(block.loc_naam);
      const queryPlace = encodeURIComponent(`${locAdress}, ${block.loc_postcode}, ${block.loc_stad}`);
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${query}&query_place_id=${queryPlace}`;
      return (<div itemScope itemType="https://schema.org/LocalBusiness" className={className}
        ref={block.loc_naam}
        data-id={block.loc_naam}
        onMouseEnter={(): void => events.emit('SearchHover', block.loc_naam)}>
        <div className="search-results__item-header">
          <div className="search-results__item-title">
            {block.loc_stad}, {block.loc_straat} {block.loc_nr}
            <meta itemProp="name" content={block.loc_naam} />
            <meta itemProp="latitude" content={block.loc_lat} />
            <meta itemProp="longitude" content={block.loc_long} />
            <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
              <meta itemProp="streetAddress" content={locAdress} />
              <meta itemProp="postalCode" content={block.loc_postcode} />
              <meta itemProp="addressLocality" content={block.loc_stad} />
            </div>
            <meta itemProp="telephone" content={block.loc_telefoon} />
            <meta itemProp="logo" content="https://www.lyceo.nl/wp-content/themes/lyceo-2015/images/lyceo-logo.svg" />
            <meta itemProp="url" content={locUrl} />
            <meta itemProp="priceRange" content="Not Applicable" />
            <span style={org_style}
              itemProp="parentOrganization"
              itemScope
              itemType="https://schema.org/Organization">
              <a itemProp="url" href="https://lyceo.nl/"><span itemProp="name">Lyceo</span>
              </a>
            </span>
          </div>
          <div className="search-results__item-route">
            {this.state.distance && (
              <span className="search-results__item-distance">
                      afstand: {this.state.distance}
              </span>
            )}
            <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer">
                Route<i className="fa-solid fa-route"></i>
            </a>
          </div>
        </div>
        <div className="search-results__item-table">
          {trainingRows}
        </div>
        {trainingDates.length > limit
              && <ShowCompleteListButton
                viewAll={viewAll}
                total={trainingDates.length}
                onClicked={(): void => this.toggleRows()} />}
      </div>);
    }
    return (<div className="search-results__item">
      <div className="search-results__item-table">
        {trainingRows}
      </div>
      {trainingDates.length > limit
                && <ShowCompleteListButton
                  viewAll={viewAll}
                  total={trainingDates.length}
                  onClicked={(): void => this.toggleRows()}/>}
    </div>);

  }
}

interface ShowCompleteListButtonProps {
  total: number;
  viewAll: boolean;
  onClicked: () => void;
}

export class ShowCompleteListButton extends Component<ShowCompleteListButtonProps> {
  public render(): JSX.Element {
    const { total, viewAll, onClicked } = this.props;
    return (
      <div className="search-results__item-text" onClick={(): void => onClicked()}>
        <span className="search-results__link">
          {viewAll
            ? 'Minder weergeven'
            : `Alle ${total} trainingen weergeven`
          }
        </span>
      </div>
    );
  }
}
